import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Form,
  FormGroup,
  Label,
  InputGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { 
  getAllUsersCredits, 
  addUserCredit 
} from "../../helpers/fakebackend_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const CreditManagement = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [formData, setFormData] = useState({
    user_id: "",
    amount: "",
    description: ""
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Additional state for pagination display
  const [fromItem, setFromItem] = useState(1);
  const [toItem, setToItem] = useState(10);

  useEffect(() => {
    fetchUsersCredit();
  }, [currentPage, perPage]);

  const fetchUsersCredit = async (email = "") => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: perPage
      };
      
      if (email) {
        params.email = email;
      }
      
      const response = await getAllUsersCredits(params);
      setUsersData(response.users || []);
      
      // Update pagination data from API response
      if (response.pagination) {
        setTotalPages(response.pagination.last_page || 1);
        setTotalItems(response.pagination.total || 0);
        setCurrentPage(response.pagination.current_page || 1);
        setFromItem(response.pagination.from || 0);
        setToItem(response.pagination.to || 0);
      }
    } catch (error) {
      console.error("Error fetching users credit data:", error);
      toastr.error("Failed to fetch users credit data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on new search
    fetchUsersCredit(searchEmail);
  };

  const handleClearSearch = () => {
    setSearchEmail("");
    setCurrentPage(1); // Reset to first page
    fetchUsersCredit();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePerPageChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const toggle = () => {
    setModal(!modal);
    if (modal) {
      setFormData({
        user_id: "",
        amount: "",
        description: ""
      });
    }
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    // Ensure user_id is set correctly before submission
    const submissionData = {
      ...formData,
      user_id: selectedUser?.user?.id || formData.user_id
    };
    console.log("Submitting form data:", submissionData);
    setLoading(true);
    try {
      await addUserCredit(submissionData);
      toggle();
      toastr.success("Credit added successfully");
      fetchUsersCredit(searchEmail);
    } catch (error) {
      console.error("Error adding credit:", error);
      toastr.error("Failed to add credit");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openAddCreditModal = (user) => {
    console.log("Opening modal for user:", user);
    setSelectedUser(user);
    setFormData({
      user_id: user.user.id,
      amount: "",
      description: ""
    });
    console.log("Set form data with user_id:", user.user.id);
    toggle();
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  document.title = "Credit Management | Admin";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Credit Management" pageTitle="Credits" />

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Search Users</h4>
                  <Form className="row gy-2 gx-3 align-items-center">
                    <div className="col-sm-auto">
                      <FormGroup className="mb-0">
                        <Label className="visually-hidden" for="searchEmail">
                          Email
                        </Label>
                        <InputGroup>
                          <Input
                            type="text"
                            className="form-control"
                            id="searchEmail"
                            placeholder="Search by email"
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            onKeyPress={handleKeyPress}
                          />
                        </InputGroup>
                      </FormGroup>
                    </div>
                    <div className="col-sm-auto">
                      <Button
                        type="button"
                        color="primary"
                        className="w-md"
                        onClick={handleSearch}
                      >
                        Search
                      </Button>
                    </div>
                    <div className="col-sm-auto">
                      <Button
                        type="button"
                        color="secondary"
                        className="w-md"
                        onClick={handleClearSearch}
                      >
                        Clear
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">User Credits</h4>
                    <div className="d-flex align-items-center">
                      <span className="me-2">Items per page:</span>
                      <Input
                        type="select"
                        name="perPage"
                        id="perPage"
                        value={perPage}
                        onChange={handlePerPageChange}
                        style={{ width: '70px' }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </Input>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <Thead className="table-light">
                        <Tr>
                          <Th scope="col">User ID</Th>
                          <Th scope="col">Name</Th>
                          <Th scope="col">Email</Th>
                          <Th scope="col">Credit Balance</Th>
                          <Th scope="col">Free Credits</Th>
                          <Th scope="col">Used Free Credits</Th>
                          <Th scope="col">Next Reset Date</Th>
                          <Th scope="col">Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {loading ? (
                          <Tr>
                            <Td colSpan="8" className="text-center">
                              Loading...
                            </Td>
                          </Tr>
                        ) : usersData.length > 0 ? (
                          usersData.map((item, index) => (
                            <Tr key={index}>
                              <Td>{item.user.id}</Td>
                              <Td>{item.user.name}</Td>
                              <Td>{item.user.email}</Td>
                              <Td>{item.credit.credit_balance}</Td>
                              <Td>{item.credit.monthly_free_credits}</Td>
                              <Td>{item.credit.monthly_credits_used}</Td>
                              <Td>
                                {new Date(item.credit.monthly_credits_reset_date).toLocaleDateString()}
                              </Td>
                              <Td>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="success"
                                    className="btn-sm"
                                    onClick={() => openAddCreditModal(item)}
                                  >
                                    Add Credit
                                  </Button>
                                  <Link
                                    to={`/credit-management/transactions/${item.user.id}`}
                                    className="btn btn-primary btn-sm"
                                  >
                                    Transactions
                                  </Link>
                                </div>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan="8" className="text-center">
                              No data available
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  {usersData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        Showing {fromItem} to {toItem} of {totalItems} entries
                      </div>
                      <Pagination>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                        </PaginationItem>
                        
                        {getPageNumbers().map(pageNumber => (
                          <PaginationItem key={pageNumber} active={pageNumber === currentPage}>
                            <PaginationLink onClick={() => handlePageChange(pageNumber)}>
                              {pageNumber}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Credit Modal */}
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          Add Credit to {selectedUser?.user.name}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleAddCredit}>
            <input
              type="hidden"
              name="user_id"
              value={selectedUser?.user?.id || ""}
            />
            <FormGroup>
              <Label for="amount">Amount</Label>
              <Input
                type="number"
                name="amount"
                id="amount"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="text"
                name="description"
                id="description"
                placeholder="Enter description"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Credit"}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

CreditManagement.propTypes = {
  match: PropTypes.object,
};

export default connect(null, null)(CreditManagement); 